import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User } from '@/types'
import { authService } from '@/services/authService'
import apiClient from '@/services/apiClient'

function getApiErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null) {
    const maybeError = error as { response?: { data?: { error?: unknown } } }
    if (typeof maybeError.response?.data?.error === 'string') {
      return maybeError.response.data.error
    }
  }
  return fallback
}

function getApiErrorStatus(error: unknown): number | null {
  if (typeof error === 'object' && error !== null) {
    const maybeError = error as { response?: { status?: unknown } }
    if (typeof maybeError.response?.status === 'number') {
      return maybeError.response.status
    }
  }
  return null
}

/**
 * Written by whichever tab completes the Atlassian OAuth round trip. A chat that paused on a
 * reconnect prompt lives in a *different* tab, so it watches these two channels to notice the
 * connection came back without the user having to reload. localStorage `storage` events only fire
 * in other tabs, which is exactly the audience here; BroadcastChannel covers the same ground and is
 * kept alongside it because Safari has historically been unreliable about one or the other.
 */
export const ATLASSIAN_CONNECTED_STORAGE_KEY = 'relay_atlassian_connected_at'
export const ATLASSIAN_CONNECTED_CHANNEL = 'relay-atlassian-connected'

/** Tell other tabs that Atlassian is connected again. Safe to call when nobody is listening. */
export function announceAtlassianConnected() {
  try {
    localStorage.setItem(ATLASSIAN_CONNECTED_STORAGE_KEY, String(Date.now()))
  } catch {
    // Private browsing / storage disabled — BroadcastChannel below may still get through.
  }
  try {
    const channel = new BroadcastChannel(ATLASSIAN_CONNECTED_CHANNEL)
    channel.postMessage({ type: 'atlassian-connected', at: Date.now() })
    channel.close()
  } catch {
    // No BroadcastChannel support; the storage key above and the reconnect poll still cover it.
  }
}

/** Set after Disconnect so the next Connect signs out of github.com first (lets you pick a different GitHub user). */
const LS_GITHUB_NEED_BROWSER_REAUTH = 'relay_github_need_browser_reauth'
/**
 * Set when the GitHub logout window could not open as a popup; user was redirected to github.com/logout.
 * On return to Integrations, we continue the OAuth flow without another logout prompt.
 */
const LS_GITHUB_RESUME_OAUTH_AFTER_LOGOUT = 'relay_github_resume_oauth_after_logout'

const GITHUB_LOGOUT_URL = 'https://github.com/logout'

function githubBrowserReauthPending(): boolean {
  return localStorage.getItem(LS_GITHUB_NEED_BROWSER_REAUTH) === '1'
}

function waitMs(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Opens GitHub's logout endpoint so the next /login/oauth/authorize visit shows the GitHub sign-in form.
 * GitHub does not support prompt=login on OAuth apps; clearing the github.com session is the reliable approach.
 */
async function ensureGithubBrowserLogoutForNextConnect(): Promise<void> {
  const popup = window.open(
    GITHUB_LOGOUT_URL,
    'relay_github_logout',
    'width=520,height=640,noopener,noreferrer'
  )
  if (!popup) {
    localStorage.setItem(LS_GITHUB_RESUME_OAUTH_AFTER_LOGOUT, '1')
    localStorage.removeItem(LS_GITHUB_NEED_BROWSER_REAUTH)
    window.location.assign(GITHUB_LOGOUT_URL)
    return
  }

  await new Promise<void>((resolve) => {
    const started = Date.now()
    const id = window.setInterval(() => {
      if (popup.closed || Date.now() - started > 120_000) {
        window.clearInterval(id)
        resolve()
      }
    }, 250)
  })
  await waitMs(500)
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => !!authService.getToken() && !!user.value)
  const isAdmin = computed(() => user.value?.roles?.includes('ROLE_ADMIN') ?? false)
  const isSupport = computed(() =>
    (user.value?.roles?.includes('ROLE_SUPPORT') && !user.value?.roles?.includes('ROLE_ADMIN')) ?? false
  )
  const hasAtlassianConnection = computed(() => user.value?.hasAtlassianConnection ?? false)
  const hasGithubConnection = computed(() => user.value?.hasGithubToken ?? false)
  const hasZoomConnection = computed(() => user.value?.hasZoomToken ?? false)

  async function fetchCurrentUser() {
    if (!authService.getToken()) return

    isLoading.value = true
    error.value = null

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 10000)

    try {
      const response = await fetch('/api/auth/me', {
        headers: {
          Authorization: `Bearer ${authService.getToken()}`
        },
        signal: controller.signal
      })

      if (response.ok) {
        user.value = await response.json()
      } else if (response.status === 401 || response.status === 403) {
        // Confirmed invalid/expired credentials — actually log out.
        logout()
      } else {
        // Some other failure (5xx, etc.) — the token itself may still be fine;
        // don't wipe a valid session over a transient backend error.
        error.value = 'Failed to fetch user'
      }
    } catch {
      // Network error or timeout — same reasoning: leave the token alone.
      error.value = 'Failed to fetch user'
    } finally {
      clearTimeout(timeoutId)
      isLoading.value = false
    }
  }

  /**
   * Adopts a new access token and refreshes the claim-backed parts of `user` from it.
   *
   * The JWT carries only `sub`, `email`, `name`, `roles`, `hasAtlassianConnection` and
   * `hasZoomToken` (see JwtTokenProvider.generateToken). Everything else on `User` comes from
   * `/api/auth/me` and cannot be reconstructed here — so anything already loaded is carried
   * forward rather than overwritten with a placeholder.
   *
   * This used to rebuild `user` wholesale, asserting `hasGithubToken: false` and dropping
   * `defaultProductId` and `teams` entirely. `teams` is the expensive one: `canManageFeature`
   * decides edit rights from it, and DashboardView decides Join vs. Leave from it, so a refresh
   * left members looking like non-members of their own teams.
   */
  function setUserFromToken(token: string, refreshToken?: string) {
    authService.setToken(token)
    if (refreshToken) {
      authService.setRefreshToken(refreshToken)
    }
    const decoded = authService.decodeToken()
    if (decoded) {
      // Only carry fields forward within the same identity. `teams` decides edit rights via
      // canManageFeature and Join/Leave in DashboardView, so merging it onto a different subject
      // would attach one user's authorization to another until /api/auth/me returns. No path does
      // that today -- AuthCallbackView runs on a full page load with an empty store -- which is
      // exactly why it should be impossible by construction rather than by caller discipline.
      const existing = user.value?.id === decoded.sub ? user.value : null
      user.value = {
        ...existing,
        id: decoded.sub,
        email: decoded.email,
        name: decoded.name,
        hasPassword: decoded.hasPassword ?? existing?.hasPassword ?? false,
        hasAtlassianConnection: decoded.hasAtlassianConnection ?? false,
        hasGithubToken: existing?.hasGithubToken ?? false,
        hasZoomToken: decoded.hasZoomToken ?? false,
        roles: decoded.roles ?? []
      }
    }
  }

  async function login(email: string, password: string): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      })

      if (response.ok) {
        const data = await response.json()
        authService.setToken(data.token)
        if (data.refreshToken) {
          authService.setRefreshToken(data.refreshToken)
        }
        user.value = data.user
        return true
      }

      let details = ''
      try {
        const contentType = response.headers.get('content-type') || ''
        if (contentType.includes('application/json')) {
          const body = await response.json()
          details = typeof body === 'string' ? body : (body?.message || '')
        } else {
          details = await response.text()
        }
      } catch {
        // Ignore parsing failures and use fallback status handling below.
      }

      if (response.status === 401) {
        error.value = details || 'Invalid credentials'
      } else {
        error.value = details || `Login failed (${response.status})`
      }
      return false
    } catch {
      error.value = 'Login failed'
      return false
    } finally {
      isLoading.value = false
    }
  }

  function logout() {
    authService.removeToken()
    user.value = null
    window.location.href = '/login'
  }

  async function refreshToken() {
    try {
      const refreshAuth = authService.getRefreshAuthorizationHeader()
      // No usable token — don't POST "Bearer null" (the backend logs it as a
      // malformed JWT, "Found: 0"); just report the refresh as failed.
      if (!refreshAuth) return false
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: {
          Authorization: refreshAuth
        }
      })

      if (response.ok) {
        const { token, refreshToken: newRefreshToken } = await response.json()
        setUserFromToken(token)
        if (newRefreshToken) {
          authService.setRefreshToken(newRefreshToken)
        }
        return true
      }
    } catch (e) {
      console.error('Token refresh failed', e)
    }
    return false
  }

  async function resolveAtlassianConnectUrl(): Promise<string> {
    try {
      const response = await apiClient.post('/api/users/me/atlassian/connect-url')
      return response.data?.url ?? '/oauth2/authorization/atlassian'
    } catch (e: unknown) {
      const status = getApiErrorStatus(e)
      if (status === 401 || status === 403) {
        const refreshed = await refreshToken()
        if (refreshed) {
          const retryResponse = await apiClient.post('/api/users/me/atlassian/connect-url')
          return retryResponse.data?.url ?? '/oauth2/authorization/atlassian'
        }
        const reloginMessage = 'Your session is no longer valid in this environment. Please log out and log back in, then reconnect Atlassian.'
        error.value = reloginMessage
        throw new Error(reloginMessage)
      }
      const message = getApiErrorMessage(e, 'Failed to start Atlassian connection')
      error.value = message
      throw new Error(message)
    }
  }

  async function connectAtlassian() {
    window.location.href = await resolveAtlassianConnectUrl()
  }

  /**
   * Reconnect without destroying the page that asked for it — used by the in-chat prompt, where
   * navigating this tab away would throw out the conversation the user is mid-way through.
   *
   * Returns the URL so the caller can offer it as a plain link. `noopener`/`noreferrer` make
   * window.open return null even when the tab opens fine, so there is no way to detect a blocked
   * popup from the return value — and falling back to navigating this tab would defeat the whole
   * point. A link the user can click is the only honest fallback.
   */
  async function connectAtlassianInNewTab(): Promise<string> {
    const url = await resolveAtlassianConnectUrl()
    window.open(url, '_blank', 'noopener,noreferrer')
    return url
  }

  async function connectZoom() {
    try {
      const response = await apiClient.post('/api/users/me/zoom/connect-url')
      const url = response.data?.url ?? '/oauth2/authorization/zoom'
      window.location.href = url
    } catch (e: unknown) {
      const status = getApiErrorStatus(e)
      if (status === 401 || status === 403) {
        const refreshed = await refreshToken()
        if (refreshed) {
          const retryResponse = await apiClient.post('/api/users/me/zoom/connect-url')
          const retryUrl = retryResponse.data?.url ?? '/oauth2/authorization/zoom'
          window.location.href = retryUrl
          return
        }
        const reloginMessage = 'Your session is no longer valid in this environment. Please log out and log back in, then reconnect Zoom.'
        error.value = reloginMessage
        throw new Error(reloginMessage)
      }
      const message = getApiErrorMessage(e, 'Failed to start Zoom connection')
      error.value = message
      throw new Error(message)
    }
  }

  function markGithubNeedsBrowserReauth() {
    localStorage.setItem(LS_GITHUB_NEED_BROWSER_REAUTH, '1')
  }

  function clearGithubBrowserReauthFlags() {
    localStorage.removeItem(LS_GITHUB_NEED_BROWSER_REAUTH)
    localStorage.removeItem(LS_GITHUB_RESUME_OAUTH_AFTER_LOGOUT)
  }

  async function postGithubConnectAndRedirect() {
    const response = await apiClient.post('/api/users/me/github/connect-url')
    localStorage.removeItem(LS_GITHUB_RESUME_OAUTH_AFTER_LOGOUT)
    const url = response.data?.url ?? '/oauth2/authorization/github'
    window.location.href = url
  }

  async function connectGithub() {
    try {
      const resumeAfterFullPageLogout =
        localStorage.getItem(LS_GITHUB_RESUME_OAUTH_AFTER_LOGOUT) === '1'

      if (!resumeAfterFullPageLogout && githubBrowserReauthPending()) {
        await ensureGithubBrowserLogoutForNextConnect()
        localStorage.removeItem(LS_GITHUB_NEED_BROWSER_REAUTH)
      }

      await postGithubConnectAndRedirect()
    } catch (e: unknown) {
      const status = getApiErrorStatus(e)
      if (status === 401 || status === 403) {
        const refreshed = await refreshToken()
        if (refreshed) {
          await postGithubConnectAndRedirect()
          return
        }
        const reloginMessage = 'Your session is no longer valid in this environment. Please log out and log back in, then reconnect GitHub.'
        error.value = reloginMessage
        throw new Error(reloginMessage)
      }
      const message = getApiErrorMessage(e, 'Failed to start GitHub connection')
      error.value = message
      throw new Error(message)
    }
  }

  return {
    user,
    isLoading,
    error,
    isAuthenticated,
    isAdmin,
    isSupport,
    hasAtlassianConnection,
    hasGithubConnection,
    hasZoomConnection,
    fetchCurrentUser,
    setUserFromToken,
    login,
    logout,
    refreshToken,
    connectAtlassian,
    connectAtlassianInNewTab,
    connectGithub,
    connectZoom,
    markGithubNeedsBrowserReauth,
    clearGithubBrowserReauthFlags
  }
})
